import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Calendar, Clock } from "lucide-react";

export default function Booking() {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "", _hp: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => api.get("/slots/available").then((r) => setSlots(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => { (map[s.date] ||= []).push(s); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [slots]);
  const dates = Object.keys(byDate).sort();

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      await api.post("/bookings", { slot_id: selectedSlot.id, ...form });
      setDone(true);
      toast.success(t("booking.success"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="eyebrow">✓</div>
        <h1 className="font-serif text-4xl mt-4 text-stone-900" data-testid="booking-success">{t("booking.success")}</h1>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="eyebrow">Book</div>
      <h1 className="font-serif text-5xl lg:text-6xl mt-6 text-stone-900" data-testid="booking-title">{t("booking.title")}</h1>
      <p className="mt-6 text-lg text-stone-700 max-w-2xl">{t("booking.sub")}</p>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif text-2xl text-stone-900 flex items-center gap-2"><Calendar size={20} /> {t("booking.chooseSlot")}</h2>
          {dates.length === 0 && <p className="mt-6 text-stone-600" data-testid="booking-no-slots">{t("booking.noSlots")}</p>}
          <div className="mt-6 space-y-3">
            {dates.map((d) => (
              <div key={d} className="border border-stone-200 bg-white p-4">
                <button
                  data-testid={`booking-date-${d}`}
                  onClick={() => setSelectedDate(selectedDate === d ? null : d)}
                  className="w-full text-left font-serif text-lg text-stone-900"
                >
                  {new Date(d).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </button>
                {selectedDate === d && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {byDate[d].map((s) => (
                      <button
                        key={s.id}
                        data-testid={`booking-slot-${s.id}`}
                        onClick={() => setSelectedSlot(s)}
                        className={`px-4 py-2 border text-sm transition-colors ${
                          selectedSlot?.id === s.id
                            ? "bg-[#D06D4D] text-white border-[#D06D4D]"
                            : "border-stone-300 text-stone-800 hover:border-stone-900"
                        }`}
                      >
                        <Clock size={12} className="inline mr-1" /> {s.start_time} — {s.end_time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="border border-stone-200 bg-white p-8">
          <h2 className="font-serif text-2xl text-stone-900">{t("booking.yourData")}</h2>
          {selectedSlot && (
            <div className="mt-4 text-sm text-stone-700 bg-stone-100 p-3">
              <strong>{selectedSlot.date}</strong> · {selectedSlot.start_time}–{selectedSlot.end_time}
            </div>
          )}
          <div className="mt-6 space-y-5">
            {[
              { k: "name", type: "text", req: true },
              { k: "email", type: "email", req: true },
              { k: "phone", type: "tel", req: true },
              { k: "reason", type: "text", req: false },
            ].map((f) => (
              <div key={f.k}>
                <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">{t(`booking.${f.k}`)}</label>
                <input
                  data-testid={`booking-${f.k}`}
                  required={f.req}
                  type={f.type}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2 text-stone-900"
                />
              </div>
            ))}
          </div>
          {/* Honeypot: hidden from real users, filled only by bots */}
          <input
            type="text"
            name="_hp"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form._hp}
            onChange={(e) => setForm({ ...form, _hp: e.target.value })}
            style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          />
          <button
            data-testid="booking-submit"
            disabled={!selectedSlot || submitting}
            className="mt-8 w-full bg-[#D06D4D] hover:bg-[#b95a3d] text-white py-3 rounded-full text-sm font-semibold disabled:opacity-40 transition-colors"
          >
            {submitting ? "…" : t("booking.confirm")}
          </button>
        </form>
      </div>
    </div>
  );
}
